/**
 * Form Persistence Hook
 * 
 * Handles localStorage persistence for form data
 * Auto-saves on form changes and restores on mount
 * Includes user/org isolation for multi-tenant support
 */

import { useEffect, useRef, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { JobPostFormData } from '../schemas/jobPostValidation';
import { JobPostMode } from '../types';
import { generateStorageKey, clearOldDrafts, getCurrentUserAndOrg } from '../utils/storageUtils';
import { validateFormData, sanitizeFormData, canSerializeData } from '../utils/dataValidation';
import { logError } from '../utils/errorHandling';

const DEBOUNCE_DELAY = 500;

export interface PersistenceStatus {
  status: 'idle' | 'saving' | 'saved' | 'error';
  lastSavedAt?: number; // Timestamp
  error?: string;
}

export const useFormPersistence = (
  form: UseFormReturn<JobPostFormData>,
  mode: JobPostMode,
  draftId: string
): PersistenceStatus => {
  const { userId, orgId } = getCurrentUserAndOrg();
  const storageKey = generateStorageKey(mode, draftId, userId, orgId);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialLoadRef = useRef(true);
  const [status, setStatus] = useState<PersistenceStatus>({ status: 'idle' });

  // Load from localStorage on mount
  // Skip loading drafts when in edit mode
  useEffect(() => {
    // Don't load drafts when editing - drafts are only for new/clone modes
    if (mode === 'edit') {
      return;
    }

    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        
        // Validate parsed data structure
        if (parsed && typeof parsed === 'object') {
          // Remove timestamp before validation (it's metadata, not form data)
          const { timestamp, ...formData } = parsed;
          
          // Validate and sanitize form data
          if (validateFormData(formData)) {
            form.reset(formData);
            isInitialLoadRef.current = false;
            // Set last saved timestamp if available
            if (timestamp) {
              setStatus({ status: 'saved', lastSavedAt: timestamp });
            }
          } else {
            // Try to sanitize corrupted data
            const sanitized = sanitizeFormData(formData);
            if (sanitized) {
              console.warn('Loaded draft had invalid structure, sanitized and restored');
              form.reset(sanitized);
              isInitialLoadRef.current = false;
              if (timestamp) {
                setStatus({ status: 'saved', lastSavedAt: timestamp });
              }
            } else {
              // Data is too corrupted, remove it
              console.error('Draft data is corrupted and cannot be restored');
              localStorage.removeItem(storageKey);
              setStatus({ status: 'error', error: 'Draft data is corrupted' });
            }
          }
        } else {
          // Invalid data format
          localStorage.removeItem(storageKey);
          setStatus({ status: 'error', error: 'Invalid draft format' });
        }
      }
    } catch (error) {
      // Log error with context
      logError('useFormPersistence.loadFromStorage', error, {
        storageKey,
        mode,
        draftId,
      });
      
      // Remove corrupted data
      try {
        localStorage.removeItem(storageKey);
      } catch (removeError) {
        console.error('Failed to remove corrupted draft:', removeError);
      }
      
      setStatus({ status: 'error', error: 'Failed to load draft' });
    }
  }, [mode]); // Include mode in dependencies

  // Save to localStorage on form changes
  // Skip saving drafts when in edit mode
  useEffect(() => {
    // Don't save drafts when editing - drafts are only for new/clone modes
    if (mode === 'edit') {
      return;
    }

    const subscription = form.watch((value) => {
      // Skip save on initial load
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
        return;
      }

      // Set saving status
      setStatus({ status: 'saving' });

      // Clear existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Debounce save
      saveTimeoutRef.current = setTimeout(() => {
        try {
          // Validate data before saving
          if (!validateFormData(value)) {
            // Try to sanitize invalid data
            const sanitized = sanitizeFormData(value);
            if (!sanitized) {
              console.error('Form data validation failed and cannot be sanitized');
              setStatus({ status: 'error', error: 'Invalid form data structure' });
              return;
            }
            // Use sanitized data
            value = sanitized;
          }
          
          // Check if data can be serialized
          if (!canSerializeData(value)) {
            console.error('Form data cannot be serialized');
            setStatus({ status: 'error', error: 'Data serialization failed' });
            return;
          }
          
          const timestamp = Date.now();
          const dataToSave = {
            ...value,
            timestamp,
          };
          
          const dataString = JSON.stringify(dataToSave);
          localStorage.setItem(storageKey, dataString);
          setStatus({ status: 'saved', lastSavedAt: timestamp });
        } catch (error: any) {
          // Log error with context
          logError('useFormPersistence.saveToStorage', error, {
            storageKey,
            mode,
            draftId,
            dataSize: JSON.stringify(value).length,
          });
          
          if (error.name === 'QuotaExceededError') {
            console.warn('Storage quota exceeded, clearing old drafts');
            clearOldDrafts(7, userId, orgId);
            try {
              // Validate and sanitize before retry
              const dataToSave = validateFormData(value) ? value : sanitizeFormData(value);
              if (!dataToSave) {
                setStatus({ status: 'error', error: 'Data validation failed' });
                return;
              }
              
              const timestamp = Date.now();
              localStorage.setItem(storageKey, JSON.stringify({
                ...dataToSave,
                timestamp,
              }));
              setStatus({ status: 'saved', lastSavedAt: timestamp });
            } catch (retryError) {
              logError('useFormPersistence.saveToStorage.retry', retryError, {
                storageKey,
                mode,
                draftId,
              });
              setStatus({ status: 'error', error: 'Storage quota exceeded' });
            }
          } else {
            setStatus({ status: 'error', error: 'Failed to save draft' });
          }
        }
      }, DEBOUNCE_DELAY);
    });

    return () => {
      subscription.unsubscribe();
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [form, storageKey, mode]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return status;
};

